import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import type { Theme, Artist } from '@tc/shared'
import { api, type ArtistWithTheme } from '../services/api'

interface ThemeContextValue {
  theme: Theme | null
  artist: Artist | null
  isLoading: boolean
  error: Error | null
}

export const ThemeContext = createContext<ThemeContextValue>({
  theme: null,
  artist: null,
  isLoading: true,
  error: null,
})

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme | null>(null)
  const [artist, setArtist] = useState<Artist | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    async function loadTheme() {
      try {
        const data = await api.getArtist()
        if (data) {
          // Theme comes embedded with artist data
          const { theme: themeData, ...artistData } = data as ArtistWithTheme
          setArtist(artistData)
          if (themeData) {
            setTheme(themeData)
            applyTheme(themeData)
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load theme'))
      } finally {
        setIsLoading(false)
      }
    }

    loadTheme()
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, artist, isLoading, error }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  return useContext(ThemeContext)
}

function applyTheme(theme: Theme | null) {
  if (!theme) return

  const root = document.documentElement

  if (theme.primaryColor) {
    root.style.setProperty('--color-primary', theme.primaryColor)
  }
  if (theme.secondaryColor) {
    root.style.setProperty('--color-secondary', theme.secondaryColor)
  }
  if (theme.backgroundColor) {
    root.style.setProperty('--color-background', theme.backgroundColor)
  }
  if (theme.textColor) {
    root.style.setProperty('--color-text', theme.textColor)
  }
  if (theme.fontFamily) {
    root.style.setProperty('--font-family', theme.fontFamily)
  }
}
